import { ReactNode } from 'react';
import { VisuallyHidden } from '@react-aria/visually-hidden';

export type SkeletonLoaderProps = {
  /** The skeleton components to display */
  children: ReactNode;
  /** Loading message for screen readers. Defaults to 'Loading content' */
  message?: string;
};

/**
 * An accessible container for skeleton loading states.
 * Wraps Skeleton components and provides proper ARIA attributes.
 *
 * @example
 * ```tsx
 * <SkeletonLoader message="Loading user profile">
 *   <Stack gap="small">
 *     <Skeleton width="100px" height="100px" borderRadius="50%" />
 *     <Skeleton width="200px" height="24px" />
 *     <Skeleton width="150px" height="16px" />
 *   </Stack>
 * </SkeletonLoader>
 * ```
 */
export const SkeletonLoader = ({
  children,
  message = 'Loading content',
}: SkeletonLoaderProps) => (
  <div aria-busy="true">
    <VisuallyHidden>{message}</VisuallyHidden>
    {children}
  </div>
);
